import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { isFiniteValue } from "@impero-com/common/utils/numeric";
import { assertNever } from "@impero-com/common/utils/obj";
import { isArray, isString, keyBy } from "lodash-es";
import memoizeOne from "memoize-one";

import {
    ExtendedF64Number,
    BinaryAssessmentStatus,
    BinaryNodeScope,
    Level,
    RiskDirectoryActions,
    AssessmentStatus,
    MitigatingControl,
    MetadataInstanceValue,
    SetRiskAssessment,
    UserOverview,
    SavedBlob,
} from "api/generated/model";
import * as R from "org/risks/types";
import { Guid } from "utils/guid";
import { assertNonEmpty } from "utils/nonempty";

import { isConditionTrue } from "./conditions";
import { FormulaComputer } from "./formulaComputer";
import { EnumMap, getResidualInstance, getEnum } from "./impactLikelihood";
import * as T from "./types";

/**
 * Utility function to build a descriptor getter from a node getter
 * Forwards getter to risk node for ExpandedRiskNode
 * @param fn the node getter
 */
export function unwrapForwardToRisk<T>(
    fn: (node: T.DirectoryNode | R.NewRiskDirectoryNode) => T
): (node: T.AnyDescriptor) => T {
    return (desc: T.AnyDescriptor) => forwardToRisk(fn)(desc.value);
}

/**
 * Forwards getter to risk node for ExpandedRiskNode
 * @param fn the node getter
 */
export function forwardToRisk<T>(
    fn: (node: T.DirectoryNode | R.NewRiskDirectoryNode) => T
): (node: T.AnyNode) => T {
    return (node: T.AnyNode) => {
        switch (node.type) {
            case "Risk":
            case "Process":
            case "RiskDirectory":
                return fn(node);
            case "ExpandedRiskNode":
                return fn(node.risk);
            default:
                assertNever(node);
        }
    };
}

// Must match MetadataPrefix in org/risks/content/types
export const METADATA_COLUMN_EXTRACTOR_ID_PREFIX = "metadata_";

/**
 * Normalize metadata column name, used to merge columns with the same name
 * @param col the MetadataField
 * @param level the metadata level
 */
export function normalizeColumn(
    col: R.MetadataField,
    level: Level
): T.MetadataColumnGroupingId {
    return `${METADATA_COLUMN_EXTRACTOR_ID_PREFIX}${level}_${encodeURIComponent(
        col.name.trim().toLocaleLowerCase()
    )}`; // `:` are forbidden in column id
}

/**
 * Convert a groupingId into an id that includes both processId and riskId.
 * Used by the save state mechanism in order to restore visible columns.
 * @param groupingId
 * @param processId
 * @param riskId
 */
export function groupingToRegularMetadataColumnId(
    groupingId: T.MetadataColumnGroupingId,
    processId: T.ProcessMetadataColumnId | undefined,
    riskId: T.RiskMetadataColumnId | undefined
): T.MetadataColumnExtractorId {
    const colReplacement = `$1_${processId ?? ""}_${riskId ?? ""}_$2`;
    const groupingIdRegex = /^(\w+_\w+)_(.+)$/;
    if (!groupingIdRegex.test(groupingId)) {
        throw new Error(
            `"${groupingId}" is not a valid MetadataColumnGroupingId`
        );
    }
    return groupingId.replace(
        groupingIdRegex,
        colReplacement
    ) as T.MetadataColumnExtractorId;
}

/**
 * Gets the entity ids directly linked to the node
 * @param node the node
 *
 * Note: Returns an empty array for Local nodes because they do not represent a new entry in risk_directory_nodes__entities
 */
export function getNodeLinkedEntities(node: T.AnyNode): number[] {
    switch (node.type) {
        case "RiskDirectory":
            return node.content.scope.entityIds;
        case "Process":
            switch (node.content.scope.type) {
                case "Global":
                case "NewGlobal":
                    return node.content.scope.entityIds;
                case "Local":
                case "NewLocal":
                    return [];
                default:
                    return assertNever(node.content.scope);
            }
        case "Risk":
            switch (node.content.scope.type) {
                case "GlobalWithContent":
                case "NewGlobal":
                    return node.content.scope.entityIds;
                case "LocalWithContent":
                case "NewLocal":
                    return [];
                default:
                    return assertNever(node.content.scope);
            }
        case "ExpandedRiskNode":
            return getNodeLinkedEntities(node.risk);
        default:
            return assertNever(node);
    }
}

/**
 * Gets the node id
 * @param node the node
 */
export function getId(node: T.AnyNode): string {
    return node.id;
}

/**
 * Gets the node guid
 * @param node the node
 */
export function getNodeGuid(node: T.AnyNode): Guid {
    return node.type === "ExpandedRiskNode" ? node.risk.id : node.id;
}

/**
 * Gets the node children
 * @param node the node
 */
export function getDirectoryChildren(node: T.DirectoryNode): T.DirectoryNode[] {
    return node.type === "Risk" ? [] : node.nodes;
}

/**
 * Gets a node path in directory
 * @param desc: the node descriptor
 */
export function getDirectoryPath(desc: DV.NodeDescriptor<T.DirectoryNode>) {
    return desc.ancestors
        .map((ancestor) => ancestor.content.name)
        .join("\xA0/\xA0")
        .replace(/ /g, "\xA0");
}

/**
 * Gets a node path fragment in directory
 * @param node: the node
 */
export function getDirectoryPathFragment(node: T.DirectoryNode) {
    return node.content.name.replace(/ /g, "\xA0");
}

/**
 * Finds and replaces a node in a sub tree.
 * @param node the sub tree
 * @param update a callback to update a given node
 * @param stopOnFirstUpdate whether or not to stop on first update
 */
export function updateDirectorySubTree(
    node: T.DirectoryNode,
    update: (node: T.DirectoryNode) => T.DirectoryNode | undefined,
    stopOnFirstUpdate: boolean
): T.DirectoryNode | undefined {
    const newNode = update(node);
    if (newNode === undefined) {
        return undefined;
    }
    if (newNode !== node && newNode.type !== node.type) {
        throw new Error(
            "Node update must not change the node type from " +
                node.type +
                " to " +
                newNode.type
        );
    }
    // Skip children if node is updated.
    // Neverthesless, the children visit is performed on "newNode" on purpose.
    if (newNode !== node) {
        return newNode;
    }
    // Visit children
    switch (newNode.type) {
        case "RiskDirectory":
        case "Process": {
            const newChildren: Array<R.StandardNode | undefined> =
                newNode.nodes.slice();
            let modified = false;
            for (
                let childIndex = 0;
                childIndex < newNode.nodes.length;
                ++childIndex
            ) {
                const childNode = newNode.nodes[childIndex];
                const newChildNode = updateDirectorySubTree(
                    childNode,
                    update,
                    stopOnFirstUpdate
                )!;
                if (newChildNode !== childNode) {
                    newChildren[childIndex] = newChildNode as
                        | typeof childNode
                        | undefined;
                    modified = true;
                    if (stopOnFirstUpdate) {
                        break;
                    }
                }
            }
            if (modified) {
                // Sort new children and remove undefined ones
                const stringCompare = Cmp.string();
                const newNewNode = {
                    ...newNode,
                    // node! is safe here because filter ensures there is no undefined values
                    nodes: newChildren
                        .filter((x) => x !== undefined)
                        .sort(
                            Cmp.map((node) => node.content.name, stringCompare)
                        ),
                };
                return newNewNode;
            } else {
                return newNode;
            }
        }
        case "Risk": {
            return newNode;
        }
        default: {
            return assertNever(newNode);
        }
    }
}

/**
 * Computes a metadata instance without a formula.
 *
 * Throws an error if the field has a formula.
 */
function getSimpleMetadataInstance(
    node: T.AnyNode,
    field: R.MetadataField,
    savedBlobs: SavedBlob[]
): T.MetadataInstance {
    const { fieldType } = field;
    switch (fieldType.type) {
        case "Dropdown":
        case "RadioButtonGroup":
        case "NumericInput":
            if (fieldType.formula !== undefined && fieldType.formula !== null) {
                throw new Error(
                    "Use getCalculatedMetadataInstance if there is a formula"
                );
            }
            break;
        default:
            break;
    }
    let maybeValue: MetadataInstanceValue | undefined;
    switch (node.type) {
        case "RiskDirectory":
        case "Process":
        case "Risk":
            maybeValue = node.content.metadataValues[field.id];
            break;
        case "ExpandedRiskNode":
            maybeValue =
                field.id in node.risk.content.metadataValues
                    ? node.risk.content.metadataValues[field.id]
                    : node.content?.metadataValues[field.id];
            break;
        default:
            return assertNever(node);
    }
    return parseMetadataInstanceValue(maybeValue, fieldType, savedBlobs);
}

function extendedNumberToNumber(num: ExtendedF64Number): number {
    switch (num.type) {
        case "NegInfinity": {
            return Number.NEGATIVE_INFINITY;
        }
        case "Infinity": {
            return Number.POSITIVE_INFINITY;
        }
        case "Number": {
            return num.value;
        }
        default: {
            return assertNever(num);
        }
    }
}

/**
 * Computes a metadata instance with a formula (or a metadata instance without one). Any variable used in
 * the formula is evaluated recursively.
 */
function getCalculatedMetadataInstance<N extends T.NodeWithMD>(
    node: N,
    savedBlobs: SavedBlob[],
    field: R.MetadataField,
    extractorIdToExtractor: DV.ExtractorIdToExtractor<N>,
    formulaComputer: FormulaComputer
): T.MetadataInstance {
    const { fieldType } = field;
    switch (fieldType.type) {
        case "Dropdown":
        case "RadioButtonGroup":
        case "NumericInput":
            if (fieldType.formula === undefined || fieldType.formula === null) {
                return getSimpleMetadataInstance(node, field, savedBlobs);
            }
            const formula = fieldType.formula;
            const numericValue = formulaComputer.evaluate(
                formula,
                (extractorId) => {
                    const extractor = extractorIdToExtractor[
                        extractorId
                    ] as DV.DataExtractor<N, T.MetadataInstance>;
                    if (!extractor) {
                        throw new Error(
                            "Can't find extractor for " + extractorId
                        );
                    }
                    const instance = extractor.getValue(node);
                    if (instance === undefined) {
                        throw new Error(
                            `No value for extractor ${extractorId} in node ${node.id}`
                        );
                    }
                    return instanceToNumericValue(instance);
                }
            );
            if (numericValue !== undefined && !isFiniteValue(numericValue)) {
                return {
                    type: "MetadataError",
                    error: "FormulaError",
                };
            }
            switch (fieldType.type) {
                case "Dropdown":
                case "RadioButtonGroup":
                    const { options } = fieldType;
                    let option: R.OptionValue | undefined;
                    if (options.length > 0) {
                        if (numericValue !== undefined) {
                            const sortedIndexAndRangeMin = options.map(
                                (opt, index) => [
                                    index,
                                    extendedNumberToNumber(opt.rangeMin),
                                ]
                            );
                            sortedIndexAndRangeMin.sort(
                                Cmp.map((tuple) => tuple[1], Cmp.primitive)
                            );
                            if (
                                sortedIndexAndRangeMin[0][1] !==
                                Number.NEGATIVE_INFINITY
                            ) {
                                console.warn(
                                    "Options' ranges do not cover the entire set of real numbers",
                                    options
                                );
                            }
                            // Find the first option which is after the matching one. `range_min` is inclusive,
                            // therefore the comparison is strict (>, and not >=).
                            const nextIndex = sortedIndexAndRangeMin.findIndex(
                                (tuple) => tuple[1] > numericValue
                            );
                            if (nextIndex > 0) {
                                option =
                                    options[
                                        sortedIndexAndRangeMin[nextIndex - 1][0]
                                    ];
                            } else if (nextIndex === 0) {
                                // WARN: Options' ranges do not cover the entire set of real numbers
                                // Yes, it is already undefined, so what?
                                option = undefined;
                            } else {
                                // Last option is a match
                                option =
                                    options[
                                        sortedIndexAndRangeMin[
                                            sortedIndexAndRangeMin.length - 1
                                        ][0]
                                    ];
                            }
                        }
                    } else {
                        console.warn(
                            `No options for field ${field.name} (${field.id})`
                        );
                    }
                    switch (fieldType.type) {
                        case "Dropdown":
                            return {
                                type: "DropdownInstance",
                                fieldType,
                                value: option,
                            };
                        case "RadioButtonGroup":
                            return {
                                type: "RadioButtonGroupInstance",
                                fieldType,
                                value: option,
                            };
                        default:
                            return assertNever(fieldType);
                    }
                case "NumericInput":
                    return {
                        type: "NumericInputInstance",
                        fieldType,
                        value: numericValue,
                    };
                default:
                    return assertNever(fieldType);
            }
        default:
            return getSimpleMetadataInstance(node, field, savedBlobs);
    }
}

/**
 * Given an instance of a field used in a formula, returns its numeric value (used for evaluating the formula).
 *
 * Throws an error if the instance type is not valid for use in a formula.
 */
function instanceToNumericValue(
    instance: T.MetadataInstance
): number | undefined {
    switch (instance.type) {
        case "DropdownInstance":
        case "RadioButtonGroupInstance":
            return instance?.value?.weight;
        case "NumericInputInstance":
            return instance.value;
        case "MetadataError":
            return undefined;
        default:
            throw new Error(
                `Fields of type ${instance.fieldType.type} may not be converted to a numeric value`
            );
    }
}

export function parseMetadataInstanceValue(
    mdValue: MetadataInstanceValue | undefined,
    fieldType: R.FieldType,
    savedBlobs: SavedBlob[]
): T.MetadataInstance {
    switch (fieldType.type) {
        case "TextArea": {
            if (mdValue !== undefined && mdValue.type !== "Text") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected Text"
                );
            }
            return {
                type: "TextAreaInstance",
                fieldType,
                value: mdValue?.value,
            };
        }
        case "TextInput": {
            if (mdValue !== undefined && mdValue.type !== "Text") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected Text"
                );
            }
            return {
                type: "TextInputInstance",
                fieldType,
                value: mdValue?.value,
            };
        }
        case "NumericInput": {
            if (mdValue !== undefined && mdValue.type !== "Number") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected Number"
                );
            }
            const value = mdValue?.value;
            if (value !== undefined && isNaN(value)) {
                throw Error("Can't parse numeric value");
            }
            return {
                type: "NumericInputInstance",
                fieldType,
                value,
            };
        }
        case "RadioButtonGroup": {
            if (mdValue !== undefined && mdValue.type !== "OptionValueId") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected OptionValueId"
                );
            }
            let value: R.OptionValue | undefined;
            if (mdValue !== undefined) {
                value = fieldType.options.find(
                    (opt) => opt.id === mdValue.value
                );
                if (value === undefined) {
                    throw new Error("Cannot find option");
                }
            } else {
                value = undefined;
            }
            return {
                type: "RadioButtonGroupInstance",
                fieldType,
                value,
            };
        }
        case "Dropdown": {
            if (mdValue !== undefined && mdValue.type !== "OptionValueId") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected OptionValueId"
                );
            }
            let value: R.OptionValue | undefined;
            if (mdValue !== undefined) {
                value = fieldType.options.find(
                    (opt) => opt.id === mdValue.value
                );
                if (value === undefined) {
                    throw new Error("Cannot find option");
                }
            } else {
                value = undefined;
            }
            return {
                type: "DropdownInstance",
                fieldType,
                value,
            };
        }
        case "CheckboxGroup": {
            if (mdValue !== undefined && mdValue.type !== "OptionValueIds") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected OptionValueIds"
                );
            }
            let value: R.OptionValue[] | undefined;
            if (mdValue !== undefined && mdValue.value.length > 0) {
                const splitValue = new Set(mdValue.value);
                value = fieldType.options.filter((opt) =>
                    splitValue.has(opt.id)
                );
                if (value === undefined) {
                    throw new Error("Cannot find option");
                }
            } else {
                value = undefined;
            }
            return {
                type: "CheckboxGroupInstance",
                fieldType,
                value,
            };
        }
        case "Heading":
            if (mdValue !== undefined) {
                throw new Error("Heading fields may not have a value");
            }
            return {
                type: "HeadingInstance",
                fieldType,
            };
        case "AttachedFiles":
            if (mdValue !== undefined && mdValue.type !== "BlobIds") {
                throw new Error(
                    mdValue.type + " is a wrong type, expected BlobIds"
                );
            }
            const value: SavedBlob[] = [];
            mdValue?.value.forEach((blobId) => {
                const foundBlob = savedBlobs.find((blob) => blob.id === blobId);
                if (foundBlob !== undefined) {
                    value.push(foundBlob);
                }
            });
            return {
                type: "AttachedFilesInstance",
                value,
                fieldType: { type: "AttachedFiles" },
            };
        default:
            return assertNever(fieldType);
    }
}

export function stringifyMetadataValueForFilter(
    value: T.MetadataValueForFilter
): string {
    if (isString(value)) {
        return value;
    } else if (isArray(value)) {
        return value.map((val) => val.name).join(", ");
    } else {
        return value.name;
    }
}

export function makeMetadataValueForFilter(
    instance: T.MetadataInstance
): T.MetadataValueForFilter {
    switch (instance.type) {
        case "MetadataError":
        case "HeadingInstance":
            // No filter on heading and error
            return "";
        case "TextInputInstance":
        case "NumericInputInstance":
        case "TextAreaInstance":
            // Regular filtering on text and number inputs
            return instance.value?.toString() ?? "";
        case "RadioButtonGroupInstance":
        case "DropdownInstance": {
            if (instance.value === undefined) {
                // No option selected => no filter
                return "";
            }
            // Otherwise we find the corresponding ProcessAndRiskOptionValueId
            // and create an IdAndName
            return { id: instance.value.id, name: instance.value.value };
        }
        case "CheckboxGroupInstance":
            if (instance.value === undefined || instance.value.length === 0) {
                // No option selected => no filter
                return "";
            }
            // Otherwise we find the corresponding ProcessAndRiskOptionValueIds
            // and create an IdAndName for each entry.
            // The expander will flatten then later.
            return instance.value.map((value) => ({
                id: value.id,
                name: value.value,
            }));
        case "AttachedFilesInstance":
            if (instance.value === undefined || instance.value.length === 0) {
                // No files attached => no filter
                return "";
            }
            return instance.value.map((file) => {
                return { id: file.id, name: file.fileName };
            });
        default:
            assertNever(instance);
    }
}

/**
 * Given a list of columns (grouping fields) and a list of "elementary" field-based metadata extractors,
 * builds a new series of extractors.
 */
export function buildColumnMetadataExtractors(
    columns: T.MetadataColumnWithId<
        T.MetadataColumnExtractorId | T.FilterMetadataColumnExtractorId
    >[],
    nodeTypeToFieldExtractors: Record<
        "Process" | "Risk",
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
    >
): DV.DataExtractor<
    DV.NodeDescriptor<T.DirectoryNode | T.MapNode>,
    T.MetadataInstance,
    T.MetadataValueForFilter
>[] {
    // The id of the extractor is the id of the field

    const extractorIdToExtractorGroup = Object.fromEntries(
        Object.entries(nodeTypeToFieldExtractors).map(([key, exts]) => [
            key,
            keyBy(exts, (ext) => ext.id),
        ])
    );

    const getExtractor = (
        col: T.MetadataColumn,
        nodeDesc: DV.NodeDescriptor<T.DirectoryNode | T.MapNode>
    ) => {
        const node = nodeDesc.value;
        let field: R.MetadataField;
        let extractorIdToExtractor: {
            [fieldId: string]: DV.DataExtractor<
                T.NodeWithMD,
                T.MetadataInstance
            >;
        };
        switch (node.type) {
            case "Process":
                if (!col.fields.Process) {
                    return undefined;
                }
                field = col.fields.Process;
                extractorIdToExtractor = extractorIdToExtractorGroup["Process"];
                break;
            case "Risk":
            case "ExpandedRiskNode":
                if (!col.fields.Risk) {
                    return undefined;
                }
                field = col.fields.Risk;
                extractorIdToExtractor = extractorIdToExtractorGroup["Risk"];
                break;
            case "RiskDirectory":
                return undefined;
            default:
                return assertNever(node);
        }
        return extractorIdToExtractor[field.id];
    };

    return columns.map((col) => {
        const asString = (
            nodeDesc: DV.NodeDescriptor<T.DirectoryNode | T.MapNode>
        ) => {
            if (nodeDesc.value.type === "RiskDirectory") {
                return "";
            }
            const extractor = getExtractor(col, nodeDesc);
            if (!extractor) {
                return "";
            }
            return extractor.asString(nodeDesc.value);
        };

        const cmpAsString = Cmp.map(asString);

        const compare = (
            item1: DV.NodeDescriptor<T.DirectoryNode | T.MapNode>,
            item2: DV.NodeDescriptor<T.DirectoryNode | T.MapNode>
        ) => {
            if (
                item1.value.type !== "RiskDirectory" &&
                item2.value.type !== "RiskDirectory"
            ) {
                const extractor = getExtractor(col, item1);
                if (extractor) {
                    return extractor.compare(item1.value, item2.value);
                }
            }
            return cmpAsString(item1, item2);
        };
        const extractor: DV.DataExtractor<
            DV.NodeDescriptor<T.DirectoryNode | T.MapNode>,
            T.MetadataInstance,
            T.MetadataValueForFilter
        > = {
            id: col.id,
            label: col.name,
            getValue: (nodeDesc) => {
                if (nodeDesc.value.type === "RiskDirectory") {
                    return undefined;
                }
                const extractor = getExtractor(col, nodeDesc);
                return extractor?.getValue(nodeDesc.value);
            },
            asString,
            compare,
            customFilterTransformer: {
                getValue: (nodeDesc) => {
                    if (nodeDesc.value.type === "RiskDirectory") {
                        // No filter on RiskDirectory
                        return "";
                    }
                    const extractor = getExtractor(col, nodeDesc);
                    const instance = extractor?.getValue(nodeDesc.value);
                    if (instance === undefined) {
                        // No filter when no instance is found
                        return "";
                    }
                    return makeMetadataValueForFilter(instance);
                },
                asString: stringifyMetadataValueForFilter,
            },
        };
        return extractor;
    });
}

export const metadataExtractorIdRegex =
    /^metadata_(riskDirectoryLevel|entityLevel)_([\w-]*)_([\w-]*)_(.+)$/;

/**
 * Construct a FilterMetadataColumnId that is resilient to column renaming.
 * Exclude the id that does not match the nodeType.
 * @param id
 * @param nodeType
 */
export function prefixFilterMetadataExtractor(
    id: T.MetadataColumnExtractorId,
    nodeType: "Process" | "Risk"
): T.FilterMetadataColumnExtractorId {
    if (!metadataExtractorIdRegex.test(id)) {
        throw new Error(`"${id}" does not match the MetadataColumnId format`);
    }

    switch (nodeType) {
        case "Risk":
            return id.replace(
                metadataExtractorIdRegex,
                "risk_metadata_$1_$3_$4"
            ) as T.FilterMetadataColumnExtractorId;
        case "Process":
            return id.replace(
                metadataExtractorIdRegex,
                "process_metadata_$1_$2_$4"
            ) as T.FilterMetadataColumnExtractorId;
        default:
            assertNever(nodeType);
    }
}

export function buildFilterMetadataExtractors(
    colsByLevel: Record<
        Level,
        T.MetadataColumnWithId<T.MetadataColumnExtractorId>[]
    >,
    nodeTypeToFieldExtractors: Record<
        "Process" | "Risk",
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
    >,
    includeEntityLevelExtractors: boolean
): DV.DataExtractor<
    DV.NodeDescriptor<T.DirectoryNode | T.MapNode>,
    T.MetadataInstance,
    T.MetadataValueForFilter
>[] {
    const rdLevelProcessMetadataExtractors = buildColumnMetadataExtractors(
        colsByLevel.riskDirectoryLevel
            .filter((col) => col.fields.Process !== undefined)
            .map((col) => {
                const { id, name, fields, ...rest } = col;
                return {
                    ...rest,
                    id: prefixFilterMetadataExtractor(id, "Process"),
                    name: t("RiskDirectory_Metadata_Label_Process", {
                        label: name,
                    }),
                    fields: {
                        Process: fields.Process,
                    },
                };
            }),
        nodeTypeToFieldExtractors
    );

    const rdLevelRiskMetadataExtractors = buildColumnMetadataExtractors(
        colsByLevel.riskDirectoryLevel
            .filter((col) => col.fields.Risk !== undefined)
            .map((col) => {
                const { id, name, fields, ...rest } = col;
                return {
                    ...rest,
                    id: prefixFilterMetadataExtractor(id, "Risk"),
                    name: t("RiskDirectory_Metadata_Label_Risk", {
                        label: name,
                    }),
                    fields: {
                        Risk: fields.Risk,
                    },
                };
            }),
        nodeTypeToFieldExtractors
    );

    const entityLevelMetadataExtractors = buildColumnMetadataExtractors(
        colsByLevel.entityLevel
            .filter((col) => col.fields.Risk !== undefined)
            .map((col) => {
                const { id, name, fields, ...rest } = col;
                return {
                    ...rest,
                    id: prefixFilterMetadataExtractor(id, "Risk"),
                    name,
                    fields: {
                        Risk: fields.Risk,
                    },
                };
            }),
        nodeTypeToFieldExtractors
    );

    const rdLevelExtractors = rdLevelProcessMetadataExtractors.concat(
        rdLevelRiskMetadataExtractors
    );
    return includeEntityLevelExtractors
        ? rdLevelExtractors.concat(entityLevelMetadataExtractors)
        : rdLevelExtractors;
}

type DropdownInstanceOrMetadataError = T.DropdownInstance | T.MetadataError;

/**
 * Given a metadata field, a risk directory, the structure mapping response options
 * to the `ResponseEnum` enum, and a formula computer, returns a data extractor spec
 * for building a data extractor.
 *
 * This also handles the special residual impact and residual likelihood columns.
 */
export function buildNodeMetadataExtractorSpec(
    field: R.MetadataField,
    riskDirectory: R.RiskDirectoryView,
    savedBlobs: SavedBlob[],
    responseEnumMap: EnumMap<R.ResponseEnum>,
    formulaComputer: FormulaComputer
): DV.DataExtractorSpec<T.NodeWithMD, T.MetadataInstance> {
    const {
        content: {
            resource: {
                structure: { blessedFieldIds },
            },
        },
    } = riskDirectory;
    const {
        inherentImpactId,
        inherentLikelihoodId,
        responseId,
        residualImpactId,
        residualLikelihoodId,
    } = blessedFieldIds;
    // Dependencies for all formulas of all fields grouped in the column
    const formulaDependencies = getDependentColumnIds(field, formulaComputer);
    const dependentExtractorIds: Array<Guid> = [...formulaDependencies];
    const stringCompare = Cmp.string();
    if (field.id === residualImpactId || field.id === residualLikelihoodId) {
        // In this case, this is a complex extractor, which depends on the value of the response, and where the value is either:
        // - the actual value of the residual metadata field
        // - or the value of the "fallback" (non-residual) corresponding column
        let fallbackColumnId: Guid;
        if (field.id === residualImpactId) {
            fallbackColumnId = inherentImpactId;
        } else if (field.id === residualLikelihoodId) {
            fallbackColumnId = inherentLikelihoodId;
        } else {
            throw new Error("Unreachable");
        }
        dependentExtractorIds.push(fallbackColumnId);
        dependentExtractorIds.push(responseId);
        const builder: DV.DependentDataExtractorSpec<
            T.NodeWithMD,
            T.MetadataInstance
        > = {
            type: "Dependent",
            dependentExtractorIds,
            extractorId: field.id,
            builder: (extractorIdToExtractor) => {
                const responseExtractor = extractorIdToExtractor[
                    responseId
                ] as DV.DataExtractor<
                    T.NodeWithMD,
                    DropdownInstanceOrMetadataError
                >;
                const fallbackExtractor = extractorIdToExtractor[
                    fallbackColumnId
                ] as DV.DataExtractor<
                    T.NodeWithMD,
                    DropdownInstanceOrMetadataError
                >;
                const getValue = (node: T.NodeWithMD) => {
                    // Safe since we just asserted above
                    const { fieldType } = field;
                    if (fieldType.type !== "Dropdown") {
                        throw new Error(
                            "Residual impact/likelihood should be dropdowns"
                        );
                    }
                    const responseInstance = responseExtractor.getValue(node);
                    // Non-residual impact or likelihood
                    const defaultInstance = fallbackExtractor.getValue(node);
                    if (
                        responseInstance === undefined ||
                        defaultInstance === undefined
                    ) {
                        throw new Error(
                            "We should have instance of the response and the fallback column"
                        );
                    }
                    if (
                        defaultInstance.type !== "MetadataError" &&
                        defaultInstance.fieldType.options.length !==
                            fieldType.options.length
                    ) {
                        throw new Error(
                            "The default instance and the residual instance should have the same number of options"
                        );
                    }
                    const defaultOptionIndex: number | undefined =
                        defaultInstance.type !== "MetadataError" &&
                        defaultInstance.value !== undefined
                            ? defaultInstance.fieldType.options.findIndex(
                                  (opt) => opt.id === defaultInstance.value!.id
                              )
                            : undefined;
                    if (
                        defaultOptionIndex !== undefined &&
                        defaultOptionIndex < 0
                    ) {
                        throw new Error("Unknown default option");
                    }

                    const fallbackInstance: T.DropdownInstance = {
                        type: "DropdownInstance",
                        fieldType,
                        value:
                            defaultOptionIndex !== undefined
                                ? fieldType.options[defaultOptionIndex]
                                : undefined,
                    };
                    let responseEnum: R.ResponseEnum | undefined;
                    switch (responseInstance.type) {
                        case "DropdownInstance":
                            responseEnum = getEnum(
                                responseInstance,
                                responseEnumMap
                            );
                            break;
                        case "MetadataError":
                            responseEnum = undefined;
                            break;
                        default:
                            assertNever(responseInstance);
                    }
                    return getResidualInstance(
                        responseEnum,
                        fallbackInstance,
                        // Callback to build the actual instance if response == Reduce
                        // The cast is safe since we asserted that the field is a Dropdown
                        () =>
                            getCalculatedMetadataInstance(
                                node,
                                savedBlobs,
                                field,
                                extractorIdToExtractor,
                                formulaComputer
                            ) as T.DropdownInstance,
                        // Callback to build an instance with a value of "undefined" if
                        // response == NotApplicable/Avoid/Transfer
                        () => ({
                            type: "DropdownInstance",
                            fieldType,
                            value: undefined,
                        })
                    );
                };
                const asString = (node: T.NodeWithMD) => {
                    const instance = getValue(node);
                    return instanceAsString(instance) || "";
                };
                const asWeight = (node: T.NodeWithMD) => {
                    const instance = getValue(node);
                    return instance.value?.weight;
                };
                const compare = Cmp.map(
                    asWeight,
                    Cmp.undefinedLast(Cmp.primitive)
                );
                return {
                    id: field.id,
                    label: field.name,
                    getValue,
                    asString,
                    compare,
                };
            },
        };
        return builder;
    }
    if (dependentExtractorIds.length === 0) {
        const hasNoFormula = !hasFormula(field.fieldType);
        const getValue = hasNoFormula
            ? (node: T.NodeWithMD) => {
                  return getSimpleMetadataInstance(node, field, savedBlobs);
              }
            : (node: T.NodeWithMD) => {
                  // No dependent extractor but a formula -> constant formula
                  return getCalculatedMetadataInstance(
                      node,
                      savedBlobs,
                      field,
                      {},
                      formulaComputer
                  );
              };
        const asString = (node: T.NodeWithMD) => {
            const instance = getValue(node);
            return instanceAsString(instance) || "";
        };
        const asWeight = (node: T.NodeWithMD) => {
            const instance = getValue(node);
            if (
                instance.type === "DropdownInstance" ||
                instance.type === "RadioButtonGroupInstance"
            ) {
                return instance.value?.weight;
            }
            return undefined;
        };
        let compare = Cmp.map(asString, Cmp.undefinedLast(stringCompare));
        if (
            field.id === inherentImpactId ||
            field.id === inherentLikelihoodId
        ) {
            compare = Cmp.map(asWeight, Cmp.undefinedLast(Cmp.primitive));
        }
        return {
            type: "Simple",
            extractor: {
                id: field.id,
                label: field.name,
                getValue,
                asString,
                compare,
            },
        };
    } else {
        return {
            type: "Dependent",
            dependentExtractorIds,
            extractorId: field.id,
            builder: (extractorIdToExtractor) => {
                const getValue = (node: T.NodeWithMD) => {
                    return getCalculatedMetadataInstance(
                        node,
                        savedBlobs,
                        field,
                        extractorIdToExtractor,
                        formulaComputer
                    );
                };
                const asString = (node: T.NodeWithMD) => {
                    const instance = getValue(node);
                    return instanceAsString(instance) || "";
                };
                const asWeight = (node: T.NodeWithMD) => {
                    const instance = getValue(node);
                    if (
                        instance.type === "DropdownInstance" ||
                        instance.type === "RadioButtonGroupInstance"
                    ) {
                        return instance.value?.weight;
                    }
                    return undefined;
                };
                let compare = Cmp.map(
                    asString,
                    Cmp.undefinedLast(stringCompare)
                );
                if (
                    field.id === inherentImpactId ||
                    field.id === inherentLikelihoodId
                ) {
                    compare = Cmp.map(
                        asWeight,
                        Cmp.undefinedLast(Cmp.primitive)
                    );
                }
                return {
                    id: field.id,
                    label: field.name,
                    getValue,
                    asString,
                    compare,
                };
            },
        };
    }
}

export function instanceAsString(
    instance: T.MetadataInstance | undefined
): string | undefined {
    if (instance === undefined) {
        return undefined;
    }
    switch (instance.type) {
        case "TextInputInstance":
        case "TextAreaInstance":
            return sanitizeWhiteSpaces(instance.value);
        case "NumericInputInstance":
            if (instance.value !== undefined) {
                return formatNumber(instance.value);
            } else {
                return undefined;
            }
        case "DropdownInstance":
        case "RadioButtonGroupInstance":
            return instance.value?.value;
        case "CheckboxGroupInstance":
            if (instance.value === undefined) {
                return undefined;
            }
            return instance.value.map((opt) => opt.value).join(", ");
        case "HeadingInstance":
            throw new Error(`Couldn't render Heading column`);
        case "MetadataError":
            switch (instance.error) {
                case "FormulaError":
                    return t("RiskDirectory_Error_Formula");
                default:
                    return assertNever(instance.error);
            }
        case "AttachedFilesInstance":
            if (instance.value === undefined || instance.value.length === 0) {
                return undefined;
            }
            return instance.value.map((blob) => blob.fileName).join(", ");
        default:
            return assertNever(instance);
    }
}

/**
 * Extracts a set of data following the condition provided by the extractor. When
 * a node has successfully extracted a data, it signals it to the extractor for all children nodes.
 *
 * @param subtree The subtree to process
 * @param extractor The extractor to apply: node to map + should we force the value to be extracted
 * @param force Flag set when a parent has been successfully extracted
 * @param data The extraction set, will get mutated.
 */
export function getSubsequentIds(subtree: T.DirectoryNode, data: Set<Guid>) {
    data.add(subtree.id);
    switch (subtree.type) {
        case "RiskDirectory":
        case "Process":
            subtree.nodes.forEach((n) => getSubsequentIds(n, data));
            break;
        case "Risk":
            break;
    }
}

/**
 * Discriminate Global nodes and Local ones
 * @param node
 */
export function getScope(node: T.AnyNode): T.NodeScope {
    switch (node.type) {
        case "RiskDirectory":
            return { scope: "Global" };
        case "Process": {
            const { scope } = node.content;
            switch (scope.type) {
                case "Global":
                case "NewGlobal":
                    return { scope: "Global" };
                case "Local":
                case "NewLocal":
                    return { scope: "Local", entityId: scope.entityId };
                default:
                    return assertNever(scope);
            }
        }
        case "Risk": {
            const { scope } = node.content;
            switch (scope.type) {
                case "GlobalWithContent":
                case "NewGlobal":
                    return { scope: "Global" };
                case "LocalWithContent":
                case "NewLocal":
                    return { scope: "Local", entityId: scope.entityId };
                default:
                    return assertNever(scope);
            }
        }
        case "ExpandedRiskNode":
            return getScope(node.risk);
        default:
            assertNever(node);
    }
}

/**
 * Discriminate Global nodes and Local ones,
 * returns undefined for non-risk nodes
 *
 * Use for filtering purposes only
 *
 * @param node
 */
export function getFilterScope(node: T.AnyNode): T.NodeScope | undefined {
    switch (node.type) {
        case "ExpandedRiskNode":
        case "Risk":
            return getScope(node);
        case "Process":
        case "RiskDirectory":
            return undefined;
        default:
            assertNever(node);
    }
}

/**
 * Localize a scope
 * @param scope
 */
export function localizeScope(scope: BinaryNodeScope): string {
    switch (scope) {
        case "Global":
            return t("RiskDirectory_Scope_Global");
        case "Local":
            return t("RiskDirectory_Scope_Local");
        default:
            assertNever(scope);
    }
}

// Calls to t are memory intensive, we should avoid them in frequently called functions used in memory heavy pages, like the risk map
const memoizedGlobalFilterScope = memoizeOne(() =>
    t("RiskDirectory_Scope_GlobalRisks")
);
const memoizedLocalFilterScope = memoizeOne(() =>
    t("RiskDirectory_Scope_LocalRisks")
);
const memoizedAssessed = memoizeOne(() => t("RiskDirectory_Assessed"));
const memoizedNotAssessed = memoizeOne(() => t("RiskDirectory_NotAssessed"));

/**
 * Localize a scope
 *
 * Use for filtering purposes only
 *
 * @param scope
 */
export function localizeFilterScope(
    scope: BinaryNodeScope | undefined
): string {
    switch (scope) {
        case "Global":
            return memoizedGlobalFilterScope();
        case "Local":
            return memoizedLocalFilterScope();
        case undefined:
            return "";
        default:
            assertNever(scope);
    }
}

/**
 * Localize assessment status type
 *
 * Use for filtering purposes only
 *
 * @param status
 */
export function localizeAssessmentStatus(
    status: AssessmentStatus["type"] | undefined
): string {
    switch (status) {
        case "Assessed":
            return memoizedAssessed();
        case "PreviouslyAssessed":
        case "NeverAssessed":
            return memoizedNotAssessed();
        case undefined:
            return "";
        default:
            assertNever(status);
    }
}

export function localizeAssessmentStatusFilter(
    status: BinaryAssessmentStatus | undefined
): string {
    switch (status) {
        case "Assessed":
            return memoizedAssessed();
        case "NotAssessed":
            return memoizedNotAssessed();
        case undefined:
            return "";
        default:
            assertNever(status);
    }
}

function getUserOverviewResource(
    rootNodeDto: T.RiskDirectoryNodeDtoWithChanges,
    userId: number
): UserOverview {
    const userOverview = rootNodeDto.linkedResources.users.find(
        (user) => user.id === userId
    );
    if (!userOverview) {
        throw new Error(
            `Expected to find corresponding user resource for id ${userId}`
        );
    }
    return userOverview;
}

/**
 * Get formatted user name of assessment status
 * @param node
 * @param riskDirectory
 */
export function getAssessmentUserName(
    node: T.MapNode,
    rootNodeDto: T.RiskDirectoryNodeDtoWithChanges
) {
    const userId = getAssessmentUser(node);
    if (!userId) {
        return "";
    }
    const userOverview = getUserOverviewResource(rootNodeDto, userId);
    const formattedUser = formatUserName(userOverview);
    return formattedUser;
}

export type NodeAssessmentStatus = AssessmentStatus["type"];

/**
 * Get node assessment status type
 * @param node
 */
export function getAssessmentStatus(
    node: T.MapNode
): NodeAssessmentStatus | undefined {
    switch (node.type) {
        case "ExpandedRiskNode":
            return node.content?.assessmentStatus.type ?? "NeverAssessed";
        case "Process":
        case "RiskDirectory":
            return undefined;
        default:
            return assertNever(node);
    }
}

export function assessmentStatusFilter(
    status: NodeAssessmentStatus | undefined
): BinaryAssessmentStatus | undefined {
    switch (status) {
        case "Assessed":
            return "Assessed";
        case "PreviouslyAssessed":
        case "NeverAssessed":
            return "NotAssessed";
        case undefined:
            return undefined;
        default:
            return assertNever(status);
    }
}

/**
 * Get node last assessment date
 * @param node
 */
export function getAssessmentDate(node: T.MapNode): string | undefined {
    switch (node.type) {
        case "ExpandedRiskNode":
            const assessmentStatus = node.content?.assessmentStatus;
            if (!assessmentStatus) {
                return undefined;
            }
            switch (assessmentStatus.type) {
                case "Assessed":
                case "PreviouslyAssessed":
                    return assessmentStatus.at;
                case "NeverAssessed":
                    return undefined;
                default:
                    return assertNever(assessmentStatus);
            }
        case "Process":
        case "RiskDirectory":
            return undefined;
        default:
            return assertNever(node);
    }
}

/**
 * Format assessment date to string
 * @param node
 */
export function localizeAssessmentDate(node: T.MapNode): string {
    const date = getAssessmentDate(node);
    if (!date) {
        return "";
    }
    return date;
}

/**
 * Get node last assessment user
 * @param node
 */
export function getAssessmentUser(node: T.MapNode): number | undefined {
    switch (node.type) {
        case "ExpandedRiskNode":
            const assessmentStatus = node.content?.assessmentStatus;
            if (!assessmentStatus) {
                return undefined;
            }
            switch (assessmentStatus.type) {
                case "Assessed":
                case "PreviouslyAssessed":
                    return assessmentStatus.userId;
                case "NeverAssessed":
                    return undefined;
                default:
                    return assertNever(assessmentStatus);
            }
        case "Process":
        case "RiskDirectory":
            return undefined;
        default:
            return assertNever(node);
    }
}

/**
 * Check if current assessment status is compatible with the new assessment status
 *
 * @param setRiskAssessment
 *
 * @param setRiskAssessment
 */
export function canChangeAssessmentStatus(
    setRiskAssessment: SetRiskAssessment,
    currentStatus: AssessmentStatus["type"]
) {
    switch (currentStatus) {
        case "Assessed":
            return setRiskAssessment === "unassessed";
        case "NeverAssessed":
        case "PreviouslyAssessed":
            return setRiskAssessment === "assessed";
        default:
            return assertNever(currentStatus);
    }
}

/**
 * Gets mitigating controls associated to a map node
 *
 * Currently don't support risk nodes because an entity id would be needed to pick the right content
 *
 * @param node
 */
export function getMitigatingControls(
    node: T.MapNode
): Array<MitigatingControl> | undefined {
    switch (node.type) {
        case "RiskDirectory":
        case "Process":
            return undefined;
        case "ExpandedRiskNode":
            return node.content?.mitigatingControls;
        default:
            assertNever(node);
    }
}

/**
 * Returns the (possibly empty) list of ids of metadata fields used in the field's formula,
 * if the field has one.
 */
export function getDependentColumnIds(
    field: R.MetadataField | undefined,
    formulaComputer: FormulaComputer
): Array<Guid> {
    let dependentColumnIds: Guid[];
    if (field === undefined) {
        dependentColumnIds = [];
    } else {
        const { fieldType } = field;
        switch (fieldType.type) {
            case "Dropdown":
            case "RadioButtonGroup":
            case "NumericInput":
                const { formula } = fieldType;
                if (formula === undefined || formula === null) {
                    dependentColumnIds = [];
                } else {
                    dependentColumnIds = formulaComputer.getVariables(formula);
                }
                break;
            case "TextArea":
            case "TextInput":
            case "CheckboxGroup":
            case "Heading":
            case "AttachedFiles":
                dependentColumnIds = [];
                break;
            default:
                return assertNever(fieldType);
        }
    }
    return dependentColumnIds;
}

export function getRiskDirectoryLevelMDFields(
    riskDirectory: R.RiskDirectoryView,
    nodeType: "Process" | "Risk"
): R.MetadataField[] {
    return (
        riskDirectory.content.resource.structure.nodeTypeToDirectoryLevelFields[
            nodeType
        ] || []
    );
}

// Note that this returns Risk-level fields by construction, since only Risk nodes have entity-level fields.
export function getEntityLevelMDFields(
    riskDirectory: R.RiskDirectoryView
): R.MetadataField[] {
    return riskDirectory.content.resource.structure.entityLevelFields;
}

export function hasFormula(fieldType: R.FieldType): boolean {
    switch (fieldType.type) {
        case "Dropdown":
        case "RadioButtonGroup":
        case "NumericInput":
            return (
                fieldType.formula !== undefined && fieldType.formula !== null
            );
        default:
            return false;
    }
}

/**
 * Build a MDField id => string map from a hash MDField id => MetadataInstance, to send back to the server
 * Instances with no value or with a formula are not included.
 */
export function extractMetadataInstanceValues(fieldIdToInstance: {
    [fieldId: string]: T.MetadataInstance | undefined;
}): { [fieldId: string]: MetadataInstanceValue } {
    const fieldIdToString: { [fieldId: string]: MetadataInstanceValue } = {};
    Object.keys(fieldIdToInstance).forEach((fieldId) => {
        const instance = fieldIdToInstance[fieldId];
        if (instance === undefined) {
            return;
        }
        const metadataInstanceValueOrNull =
            extractMetadataInstanceValue(instance);
        if (metadataInstanceValueOrNull !== null) {
            fieldIdToString[fieldId] = metadataInstanceValueOrNull;
        }
    });
    return fieldIdToString;
}

export function extractMetadataInstanceValue(
    instance: T.MetadataInstance
): MetadataInstanceValue | null {
    switch (instance.type) {
        case "HeadingInstance":
        case "MetadataError":
            return null;
        case "TextAreaInstance":
        case "TextInputInstance":
            if (instance.value === undefined) {
                return null;
            }
            return { type: "Text", value: instance.value };
        case "NumericInputInstance":
            if (instance.value === undefined) {
                return null;
            }
            return { type: "Number", value: instance.value };
        case "DropdownInstance":
        case "RadioButtonGroupInstance":
            if (instance.value === undefined) {
                return null;
            }
            return { type: "OptionValueId", value: instance.value.id };
        case "CheckboxGroupInstance":
            if (instance.value === undefined || instance.value.length === 0) {
                return null;
            }
            return {
                type: "OptionValueIds",
                value: assertNonEmpty(instance.value.map((v) => v.id)),
            };
        case "AttachedFilesInstance":
            if (instance.value === undefined || instance.value.length === 0) {
                return null;
            }
            return {
                type: "BlobIds",
                value: assertNonEmpty(instance.value.map((file) => file.id)),
            };
        default:
            return assertNever(instance);
    }
}

function getImpactLikelihoodFieldType(
    fieldId: Guid,
    fields: R.MetadataField[]
): R.DropdownField {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) {
        throw new Error(
            "Can't find impact/likelihood field with id " + fieldId
        );
    }
    if (field.fieldType.type !== "Dropdown") {
        throw new Error("Impact/likelihood fields should be dropdowns");
    }
    return field.fieldType;
}

interface ImpactLikelihoodIds {
    inherentId: Guid;
    residualId: Guid;
}

interface ImpactLikelihoodOptions {
    inherentOptions: R.OptionValue[];
    residualOptions: R.OptionValue[];
}

function getImpactLikelihoodOptions(
    impactLikelihoodIds: ImpactLikelihoodIds,
    riskDirectory: R.RiskDirectoryView
): ImpactLikelihoodOptions {
    const entityLevelFields = getEntityLevelMDFields(riskDirectory);
    const inherentFieldType = getImpactLikelihoodFieldType(
        impactLikelihoodIds.inherentId,
        entityLevelFields
    );
    const residualFieldType = getImpactLikelihoodFieldType(
        impactLikelihoodIds.residualId,
        entityLevelFields
    );
    return {
        inherentOptions: inherentFieldType.options,
        residualOptions: residualFieldType.options,
    };
}

export function assertResidualAndInherentHaveTheSameScale(
    riskDirectory: R.RiskDirectoryView
) {
    const {
        content: {
            resource: {
                structure: { blessedFieldIds },
            },
        },
    } = riskDirectory;
    const {
        inherentImpactId,
        inherentLikelihoodId,
        residualImpactId,
        residualLikelihoodId,
    } = blessedFieldIds;
    // We don't know the scale of the impact or the likelihood in advance.
    // Let's find the inherents and residuals
    {
        const impactOptions = getImpactLikelihoodOptions(
            {
                inherentId: inherentImpactId,
                residualId: residualImpactId,
            },
            riskDirectory
        );
        if (
            impactOptions.inherentOptions.length !==
            impactOptions.residualOptions.length
        ) {
            throw new Error(
                "The scale of the inherent and the residual impact should be identical"
            );
        }
    }
    {
        const likelihoodOptions = getImpactLikelihoodOptions(
            {
                inherentId: inherentLikelihoodId,
                residualId: residualLikelihoodId,
            },
            riskDirectory
        );
        if (
            likelihoodOptions.inherentOptions.length !==
            likelihoodOptions.residualOptions.length
        ) {
            throw new Error(
                "The scale of the inherent and the residual likelihood should be identical"
            );
        }
    }
}

export function computeNodeCreationPermission(
    parentNode: R.RiskDirectoryNode | R.ProcessNode,
    actions: RiskDirectoryActions,
    common: T.CommonGetters
): T.NodeCreationPermission {
    const { createGlobalNode, createLocalNode } = actions;
    const scopeAndEntityId = getScope(parentNode);

    switch (scopeAndEntityId.scope) {
        case "Local": {
            // When parent node is a local node, only local node creation is permitted,
            // if user can create a local node in this entity
            switch (createLocalNode.type) {
                case "ForAllEntities":
                    return {
                        type: "ChildOfLocalNodePermission",
                        entityId: scopeAndEntityId.entityId,
                    };
                case "ForEntities":
                    if (
                        createLocalNode.entity.includes(
                            scopeAndEntityId.entityId
                        )
                    ) {
                        return {
                            type: "ChildOfLocalNodePermission",
                            entityId: scopeAndEntityId.entityId,
                        };
                    } else {
                        return { type: "NoPermission" };
                    }
                default:
                    return assertNever(createLocalNode);
            }
        }
        case "Global": {
            // When parent node is a global node, global node creation is permitted if user can create global nodes
            // and local node creation is permitted if there is at least one entity in which user can create a local node
            // in the entities linked to the parent node or any of its ancestors.
            const entityIds = common.getComputedEntityIds(parentNode.id);
            const helper = (entityIds: number[]): T.NodeCreationPermission => {
                if (entityIds.length > 0) {
                    if (createGlobalNode) {
                        return { type: "GlobalAndLocalPermission", entityIds };
                    } else {
                        return { type: "LocalOnlyPermission", entityIds };
                    }
                } else if (createGlobalNode) {
                    return { type: "GlobalOnlyPermission" };
                } else {
                    return { type: "NoPermission" };
                }
            };
            switch (createLocalNode.type) {
                case "ForAllEntities":
                    return helper(entityIds);
                case "ForEntities": {
                    const filteredEntityIds = entityIds.filter((id) =>
                        createLocalNode.entity.includes(id)
                    );
                    return helper(filteredEntityIds);
                }
                default:
                    return assertNever(createLocalNode);
            }
        }
        default:
            assertNever(scopeAndEntityId);
    }
}

export function validateNodeName(
    nodeName: string,
    nodeId: Guid,
    nodeType: "Process" | "Risk",
    nodeScope: T.NodeScope,
    parentNode: R.ParentNode
): string | undefined {
    const matchingSiblings = parentNode.nodes.filter((sibling) => {
        if (sibling.id === nodeId || sibling.type !== nodeType) {
            return false;
        } else if (nodeScope.scope === "Local") {
            const siblingScope = getScope(sibling);
            return (
                siblingScope.scope !== "Local" ||
                siblingScope.entityId === nodeScope.entityId
            );
        } else {
            return true;
        }
    });

    if (
        matchingSiblings.some((sibling) =>
            Cmp.citextEq(sibling.content.name, nodeName)
        )
    ) {
        return t("RiskDirectory_Error_ExistingNodeName");
    } else {
        return undefined;
    }
}

// "READ_ONLY" => The field can be read by dependant fields but can't be edited
// "HIDDEN" => The field can't be read by dependant fields therefore they are hidden too
type HiddenFieldStatus = "READ_ONLY" | "HIDDEN";

// XXX The problem with this function is that it does not crash on non existing fields.
//
// The first item in the result is the sections filtered according to the `keep`
// parameters.
//
// The second item in the result is the fields which may appear after the filtering but actually
// do not because of the conditions. That allows to know which fields should be reset in the
// values change.
export function filterAndResolveDirectorySections(
    riskDirectoryView: R.RiskDirectoryView,
    extractors: Array<DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>>,
    node: T.NodeWithMD,
    keep: { riskDirectoryLevel: boolean; entityLevel: boolean }
): {
    sections: Array<{
        id: Guid;
        sectionColumns: Array<
            Array<DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>>
        >;
    }>;
    hiddenFields: Map<Guid, HiddenFieldStatus>;
    matchCount: number;
} {
    // The condition handling is inspired by getHiddenTasks in legacy
    // but the legacy input flat list of tasks is here the list of fields
    // filtered according to the `keep` parameters, and in the order they
    // appear in sections.
    const fieldIndex: Record<Guid, R.MetadataField> = {};
    const hiddenFields: Map<Guid, HiddenFieldStatus> = new Map();
    let baseNode: R.NewRiskDirectoryNode | R.ProcessNode | R.RiskNode;
    switch (node.type) {
        case "ExpandedRiskNode":
            const nodeRisk: T.ExpandedRiskNode = node;
            baseNode = nodeRisk.risk;
            break;
        default:
            baseNode = node;
    }
    const extractorIdToExtractor = keyBy(extractors, (ext) => ext.id);
    let matchCount = 0;
    const sections = riskDirectoryView.content.resource.structure.sections.map(
        (section) => {
            const { sectionColumns, ...rest } = section;
            return {
                ...rest,
                sectionColumns: sectionColumns.map((column) => {
                    return column
                        .map((fieldId) => {
                            const field = findFieldById(
                                riskDirectoryView,
                                fieldId,
                                baseNode.type,
                                keep.riskDirectoryLevel,
                                keep.entityLevel
                            );
                            // XXX The field may not be found because we loop over all sections, all fields, but look the field up only
                            // for the given node type.
                            let hide = false;
                            if (field && field.condition) {
                                const dependencyFieldId =
                                    field.condition.columnId;

                                const dependency =
                                    fieldIndex[field.condition.columnId];
                                if (!dependency) {
                                    throw new Error(
                                        "Field '" +
                                            fieldId +
                                            "' erroneously depends on field '" +
                                            dependencyFieldId +
                                            "'"
                                    );
                                }

                                if (
                                    hiddenFields.get(dependencyFieldId) !==
                                    "HIDDEN"
                                ) {
                                    const dependencyExtractor =
                                        extractorIdToExtractor[
                                            dependencyFieldId
                                        ];
                                    if (!dependencyExtractor) {
                                        throw new Error(
                                            "Cannot find extractor for condition field " +
                                                dependencyFieldId
                                        );
                                    }
                                    const dependencyValue =
                                        dependencyExtractor.getValue(node);
                                    if (dependencyValue === undefined) {
                                        throw new Error(
                                            "Cannot find dependency instance for field " +
                                                dependencyFieldId
                                        );
                                    }
                                    const conditionTruth = isConditionTrue(
                                        field.id,
                                        field.condition,
                                        dependencyValue
                                    );
                                    switch (field.condition.action) {
                                        case "Show":
                                            hide = !conditionTruth;
                                            break;
                                        case "Hide":
                                            hide = conditionTruth;
                                            break;
                                        default:
                                            return assertNever(
                                                field.condition.action
                                            );
                                    }
                                } else {
                                    hide = true;
                                }
                            }
                            // Index all fields, including hidden fields
                            if (field) {
                                fieldIndex[fieldId] = field;
                            }
                            if (!hide) {
                                matchCount++;
                            } else {
                                hiddenFields.set(
                                    fieldId,
                                    field &&
                                        isAlwaysVisible(
                                            field.id,
                                            riskDirectoryView
                                        )
                                        ? "READ_ONLY"
                                        : "HIDDEN"
                                );
                            }
                            return field !== undefined && !hide
                                ? extractorIdToExtractor[fieldId]
                                : undefined;
                        })
                        .filter((extractor) => extractor !== undefined);
                }),
            };
        }
    );
    return { sections, hiddenFields, matchCount };
}

export function getFieldIdsWithoutValueForAssessment(
    riskDirectoryView: R.RiskDirectoryView,
    extractors: Array<DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>>,
    node: T.NodeWithMD
): Set<Guid> {
    switch (node.type) {
        case "ExpandedRiskNode":
            break;
        case "Risk":
            break;
        case "Process":
            throw new Error(
                `Assessment not handled for node of type ${node.type}`
            );
        default:
            return assertNever(node);
    }
    const extractorIdToExtractor = keyBy(extractors, (ext) => ext.id);
    const fieldIdsWithoutValueForAssessment: Set<Guid> = new Set();
    // For assessments, we are only interested in entity-level Risk fields
    getEntityLevelMDFields(riskDirectoryView).forEach((field) => {
        if (field.requiredForAssessment === false) {
            return;
        }
        const extractor = extractorIdToExtractor[field.id];
        const value = extractor.getValue(node);
        switch (value?.type) {
            case "HeadingInstance":
                return;
            case "TextAreaInstance":
            case "TextInputInstance":
            case "NumericInputInstance":
            case "DropdownInstance":
            case "RadioButtonGroupInstance":
            case "CheckboxGroupInstance":
                if (value.value === undefined) {
                    fieldIdsWithoutValueForAssessment.add(field.id);
                }
                break;
            case "AttachedFilesInstance":
                if (value.value === undefined || value.value.length === 0) {
                    fieldIdsWithoutValueForAssessment.add(field.id);
                }
                break;
            case "MetadataError":
            case undefined:
                fieldIdsWithoutValueForAssessment.add(field.id);
                break;
            default:
                assertNever(value);
        }
    });
    return fieldIdsWithoutValueForAssessment;
}

function findFieldById(
    riskDirectoryView: R.RiskDirectoryView,
    fieldId: Guid,
    nodeType: R.NodeTypeWithMetadata,
    inRiskLevel: boolean,
    inEntityLevel: boolean
): R.MetadataField | undefined {
    const {
        content: {
            resource: { structure },
        },
    } = riskDirectoryView;
    let field: R.MetadataField | undefined;
    if (inRiskLevel) {
        field = structure.nodeTypeToDirectoryLevelFields[nodeType]?.find(
            (field) => field.id === fieldId
        );
    }
    if (!field && inEntityLevel && nodeType === "Risk") {
        field = structure.entityLevelFields?.find(
            (field) => field.id === fieldId
        );
    }
    return field;
}

/**
 * These are "magic" fields, which are always visible, condition or not
 */
function isAlwaysVisible(
    extractorId: string,
    riskDirectoryView: R.RiskDirectoryView
): boolean {
    const {
        content: {
            resource: {
                structure: { blessedFieldIds },
            },
        },
    } = riskDirectoryView;
    return (
        extractorId === blessedFieldIds.residualImpactId ||
        extractorId === blessedFieldIds.residualLikelihoodId
    );
}

export function formatNumber(value: number): string {
    return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

export function sanitizeWhiteSpaces(value: string | undefined): string {
    return value === undefined ? "" : value.replaceAll("&#x20;", " ");
}
